/**
 * YetkiliKullaniciSecModal.jsx — Firma Kartı › Firma Yetkilileri: firmanın Firma Yetkilisini, firmanın KENDİ
 * kullanıcıları arasından seç (backlog #437, 2026-10-16).
 *
 * Mevcut "Yetkili ekle / düzenle" modalı bir kişiyi elle yazdırır; bu modal ise portal hesabı olan bir kullanıcıyı seçtirir ve
 * kaydı onun bilgileriyle doldurur: ad soyad, e-posta, telefon ve BAĞLI HESAP (`kullaniciId`). Kararlar:
 *  - "Kullanılabilir kullanıcı" = firmanın kendi girişleri (user_to_firma; `getFirmaKullanicilar`). Başka firmanın ya da
 *    personelin kullanıcısı listede yoktur; backend de `kullaniciId`'nin bu firmaya bağlı olduğunu zaten doğrular
 *    (Mesaj.FIRMA_KULLANICI_BAGLI_DEGIL).
 *  - Seçim BAĞLANTI + bilgi kopyasıdır: kullanıcı sonradan değişirse kayıt kendiliğinden değişmez (yetkilinin telefonu /
 *    e-postası kayıtta kalır; hesabın bilgileri ayrı). Kullanıcının firmayla bağı kalkarsa (#415) kayıt kalır, bağlı hesap
 *    okumada görünmez olur (backend böyle okur).
 *  - Firma Yetkilisi `tekil`dir (bir firmada bir tane): kayıt YOKSA oluşturulur, VARSA aynı kayıt GÜNCELLENİR. Güncellemede
 *    yalnızca ad, e-posta, telefon, ünvan ve bağlı hesap gider; departman, doğum tarihi, LinkedIn, biyografi ve fotoğraf
 *    DEĞİŞMEZ (PUT kısmi güncellemedir) — hiçbir şey sessizce silinmez, formda söylenir.
 *  - Rol sabit: profil grubunun rolü (#443: firma artık her rolü yönetir, `firmaYonetebilir` hepsine true; seçilen yine Firma Yetkilisi). Bugün tek rol var
 *    (`firmaTemsilcisi`, "Firma Yetkilisi"); ikinci bir rol eklenirse burada bir rol seçici gerekir.
 * Yazma yetkisi sayfada FIRMA_PROFILI ≥ WRITE'a bağlıdır (#436).
 */
import React, { useState } from "react";
import { Alert, Button, Col, Input, Label, Modal, ModalBody, ModalFooter, ModalHeader, Row, Spinner } from "reactstrap";
import { useTranslation } from "react-i18next";

/**
 * @param {Array}   kullanicilar  firmanın portal hesapları: { id, adSoyad, email, telefon }
 * @param {object}  rol           katalog rolü (kod, ad, unvanIpucu) — firmanın yönetebildiği profil grubu rolü
 * @param {object}  mevcut        bu rolün bu firmadaki mevcut kaydı (yetkili satırı) ya da null
 * @param {boolean} saving
 * @param {string}  hata          kaydetme hatası (modal içinde gösterilir; sayfanın üstündeki uyarı pencerenin arkasında kalırdı)
 * @param {Function} onKaydet     ({ rol, kullaniciId, adSoyad, email, telefon, unvan }) => Promise
 */
export default function YetkiliKullaniciSecModal({ toggle, kullanicilar = [], rol, mevcut, saving, hata, onKaydet }) {
  const { t } = useTranslation("components");
  const [kullaniciId, setKullaniciId] = useState(() => (mevcut?.kullaniciId != null ? String(mevcut.kullaniciId) : ""));
  const [adSoyad, setAdSoyad] = useState(() => mevcut?.adSoyad || "");
  const [email, setEmail] = useState(() => mevcut?.email || "");
  const [telefon, setTelefon] = useState(() => mevcut?.telefon || "");
  const [unvan, setUnvan] = useState(() => mevcut?.unvan || "");

  function kullaniciSecildi(e) {
    const id = e.target.value;
    setKullaniciId(id);
    const u = kullanicilar.find((k) => String(k.id) === id);
    if (u) {
      // Seçilen kullanıcının bilgileri forma yazılır (düzenlenebilir); boş gelen alan, yazılı olanı silmez.
      setAdSoyad(u.adSoyad || adSoyad);
      setEmail(u.email || email);
      setTelefon(u.telefon || telefon);
    }
  }

  const gecerli = kullaniciId !== "" && adSoyad.trim() !== "";

  return (
    <Modal isOpen toggle={saving ? undefined : toggle}>
      <ModalHeader toggle={saving ? undefined : toggle}>
        {t("yetkiliKullaniciSec.baslik", { rol: rol?.ad || "" })}
      </ModalHeader>
      <ModalBody>
        {hata && <Alert color="danger" className="small py-2">{hata}</Alert>}
        {kullanicilar.length === 0 ? (
          <Alert color="warning" className="small py-2 mb-0">{t("yetkiliKullaniciSec.kullaniciYok")}</Alert>
        ) : (
          <>
            {mevcut && (
              <Alert color="info" className="small py-2">
                {t("yetkiliKullaniciSec.mevcutKayit", { rol: rol?.ad || "", adSoyad: mevcut.adSoyad || "" })}
              </Alert>
            )}
            <div className="mb-2">
              <Label className="small" for="yks-kullanici">{t("yetkiliKullaniciSec.kullanici")}</Label>
              <Input id="yks-kullanici" type="select" bsSize="sm" value={kullaniciId} onChange={kullaniciSecildi}>
                <option value="">{t("yetkiliKullaniciSec.kullaniciSecin")}</option>
                {kullanicilar.map((u) => (
                  <option key={u.id} value={String(u.id)}>
                    {u.adSoyad || t("yetkiliKullaniciSec.adsizKullanici", { id: u.id })}{u.email ? ` — ${u.email}` : ""}
                  </option>
                ))}
              </Input>
              <div className="text-muted small mt-1">{t("yetkiliKullaniciSec.yalnizcaFirmaKullanicilari")}</div>
            </div>
            <div className="mb-2">
              <Label className="small" for="yks-rol">{t("yetkiliKullaniciSec.rol")}</Label>
              <Input id="yks-rol" bsSize="sm" value={rol?.ad || ""} disabled />
            </div>
            <div className="mb-2">
              <Label className="small" for="yks-ad">{t("yetkiliKullaniciSec.adSoyad")}</Label>
              <Input id="yks-ad" bsSize="sm" value={adSoyad} onChange={(e) => setAdSoyad(e.target.value)} />
            </div>
            <div className="mb-2">
              <Label className="small" for="yks-unvan">{t("yetkiliKullaniciSec.unvan")}</Label>
              <Input
                id="yks-unvan" bsSize="sm" value={unvan} placeholder={rol?.unvanIpucu || ""}
                onChange={(e) => setUnvan(e.target.value)}
              />
            </div>
            <Row className="g-2">
              <Col md={6}>
                <Label className="small" for="yks-tel">{t("yetkiliKullaniciSec.telefon")}</Label>
                <Input id="yks-tel" bsSize="sm" value={telefon} onChange={(e) => setTelefon(e.target.value)} />
              </Col>
              <Col md={6}>
                <Label className="small" for="yks-eposta">{t("yetkiliKullaniciSec.ePosta")}</Label>
                <Input id="yks-eposta" type="email" bsSize="sm" value={email} onChange={(e) => setEmail(e.target.value)} />
              </Col>
            </Row>
            {mevcut && <div className="text-muted small mt-3">{t("yetkiliKullaniciSec.digerAlanlarDegismez")}</div>}
          </>
        )}
      </ModalBody>
      <ModalFooter>
        <Button color="secondary" outline onClick={toggle} disabled={saving}>{t("common:iptal")}</Button>
        <Button
          color="primary" disabled={saving || !gecerli || kullanicilar.length === 0}
          onClick={() => onKaydet({
            rol: rol?.kod, kullaniciId: Number(kullaniciId),
            adSoyad: adSoyad.trim(), email: email.trim(), telefon: telefon.trim(), unvan: unvan.trim(),
          })}
        >
          {saving ? <Spinner size="sm" /> : t("common:kaydet")}
        </Button>
      </ModalFooter>
    </Modal>
  );
}
